export const MAX_PRODUCT_IMAGE_BYTES = 32 * 1024 * 1024;
export const MAX_PRODUCT_IMAGE_LABEL = "32 MB";

export function validateProductImageFile(file) {
  if (!file) {
    return null;
  }

  if (file.size > MAX_PRODUCT_IMAGE_BYTES) {
    return `"${file.name}" exceeds the ${MAX_PRODUCT_IMAGE_LABEL} limit.`;
  }

  return null;
}

/**
 * Turns an Axios upload error into a clear, actionable message for the
 * intermittent failures that image/document uploads hit in production:
 * oversized payloads rejected by the server (413), timeouts, and network
 * drops. Returns null when the error isn't one of these, so callers can fall
 * back to field-level validation messages or the raw error.
 */
export function describeUploadError(error) {
  if (!error) {
    return null;
  }

  const status = error.response?.status;

  // 413 from nginx (client_max_body_size) or Laravel's PostTooLargeException.
  // nginx returns an HTML body, so there is usually no JSON `message` to show.
  if (status === 413) {
    return `The upload is too large for the server to accept in one request. Upload fewer images, or images under ${MAX_PRODUCT_IMAGE_LABEL} each, and try again.`;
  }

  // Axios aborts on timeout with code ECONNABORTED (the multipart requests use
  // an extended 5-minute timeout, so this means the upload genuinely stalled).
  if (error.code === "ECONNABORTED" || /timeout/i.test(error.message || "")) {
    return "The upload timed out before it finished. This usually means the images are large or the connection is slow — try again with fewer or smaller images.";
  }

  // No response at all → the request never reached the server.
  if (!error.response && (error.code === "ERR_NETWORK" || error.request)) {
    return "Could not reach the server to complete the upload. Check your connection and try again.";
  }

  return null;
}

// The AI editor's final output is cover-cropped to 600×800 on the server, so
// full-resolution camera files only slow the upload down and trip nginx's
// body-size limit (413). Re-encode anything big to a bounded JPEG first.
const AI_EDIT_MAX_DIMENSION = 1600;
const AI_EDIT_SKIP_BELOW_BYTES = 900 * 1024;
const AI_EDIT_JPEG_QUALITY = 0.85;

export async function downscaleImageForAiEdit(file) {
  if (!file || !/^image\/(jpe?g|png|webp)$/i.test(file.type || "")) {
    return file;
  }

  let source;
  try {
    source = await loadImageSource(file);
  } catch {
    return file;
  }

  const scale = Math.min(1, AI_EDIT_MAX_DIMENSION / Math.max(source.width, source.height));
  if (scale >= 1 && file.size <= AI_EDIT_SKIP_BELOW_BYTES) {
    if (source.close) source.close();
    return file;
  }

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(source.width * scale));
  canvas.height = Math.max(1, Math.round(source.height * scale));
  const context = canvas.getContext("2d");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  if (source.close) source.close();

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", AI_EDIT_JPEG_QUALITY));
  if (!blob) {
    return file;
  }

  const baseName = (file.name || "image").replace(/\.[^.]+$/, "");
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
}

function loadImageSource(file) {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file);
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = (event) => {
      URL.revokeObjectURL(url);
      reject(event);
    };
    image.src = url;
  });
}

export function extractProductImageUploadError(responseData) {
  const errors = responseData?.errors;
  if (!errors || typeof errors !== "object") {
    return null;
  }

  const messages = [];
  if (errors.thumbnail_image) {
    messages.push(...[].concat(errors.thumbnail_image));
  }

  Object.keys(errors)
    .filter((key) => key.startsWith("gallery_images"))
    .sort()
    .forEach((key) => {
      messages.push(...[].concat(errors[key]));
    });

  return messages.length > 0 ? messages.join(" ") : null;
}
